// @ts-strict-ignore
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {inject, Injectable, Signal, signal} from "@angular/core";
import {
	Assessable,
	AssessableItem,
	AssessableItemGroup,
	Assessment,
	AssessmentItem,
	ChecklistItemType,
	ScenarioRecording,
	UserRecordingAssessment,
	ValuedSelectableOption,
} from "@models";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {PointInputValidator} from "./point-input.validator";
import {FormArrayHelpers} from "./form-array-helpers";
import {BehaviorSubject, combineLatest, defer, Observable} from "rxjs";
import {debounceTime, filter, map, startWith, tap} from "rxjs/operators";
import {NotificationService, SimAssessmentService, SimMaterialDialogService, SimRecordingsService, TypePredicates} from "@services";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {cloneDeep, difference, merge} from "lodash-es";
import {AssessmentDataService} from "./assessment-data.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

export type RemoveItemParams = {
	itemIndex: number;
	groupIndex: number | null;
};

// Base form state, used by the AssessableGroupPanelComponent
export interface AssessableFormState<
	TItemFormState extends AssessableItemFormState & {[K in keyof TItemFormState]: AbstractControl} = AssessableItemFormState,
> {
	assessableGroups: FormArray<
		FormGroup<{
			id: FormControl<string>;
			name: FormControl<string>;
			note: FormControl<string>;
			items: FormArray<FormGroup<TItemFormState>>;
		}>
	>;
	ungroupedItems: FormArray<FormGroup<TItemFormState>>;
}

export interface AssessableItemFormState {
	id: FormControl<string>;
	title: FormControl<string>;
	note: FormControl<string>;
}

export abstract class AssessableContextService<
	TItemType extends AssessableItem,
	TItemFormState extends AssessableItemFormState & {[K in keyof TItemFormState]: AbstractControl} = AssessableItemFormState,
> {
	protected _scenarioRecording: ScenarioRecording | null = null;

	public get scenarioRecording() {
		return this._scenarioRecording;
	}

	public abstract $assessable: Observable<Assessable<TItemType>>;

	public abstract getAssessable(): Assessable<TItemType>;

	public abstract form: FormGroup<AssessableFormState<TItemFormState>>;

	public abstract moveItemToOtherGroup(itemIndex: number, groupIndex: number): Promise<void>;

	public abstract addItemToGroup(groupIndex: number): Promise<{groupId: string; itemId: string}>;

	public abstract removeItem(params: RemoveItemParams);

	public abstract addGroup();

	public abstract removeGroup(index: number);

	public abstract dropGroupPanel(event: CdkDragDrop<FormArray>);

	public abstract dropUngroupedItem(event: CdkDragDrop<FormArray>);

	public abstract dropItemInsideGroup(event: CdkDragDrop<FormArray>);

	public abstract shiftGroup(dir: "up" | "down", groupIndex: number);

	public abstract shiftGroupedItem(dir: "up" | "down", groupIndex: number, itemIndex: number);

	public abstract shiftUngroupedItem(dir: "up" | "down", itemIndex: number);

	public abstract handleItemTypeChange(item: TItemType, selectedChecklistItemType: ChecklistItemType);

	public abstract changeActivationCondition(group: AssessableItemGroup<TItemType>): Promise<void>;

	public abstract isFormValid: Signal<boolean>;

	public findItemControl(itemId: string): FormGroup<TItemFormState> {
		const groupItemControls = this.form.controls.assessableGroups.controls.flatMap((group) => group.controls.items.controls);
		const ungroupedItemControls = this.form.controls.ungroupedItems.controls;

		const itemControls = [...groupItemControls, ...ungroupedItemControls];
		return itemControls.find((control) => control.controls.id.value === itemId);
	}
}

export type AssessmentItemFormState = AssessableItemFormState & {
	selectableOptions: FormArray<
		FormGroup<{
			option: FormControl<string>;
			score: FormControl<number>;
		}>
	>;
	freeTextMaxScore: FormControl<number>;
	starScores: FormArray<FormControl<number>>;
	yesNoScores: FormArray<FormControl<number>>;
	competenceCategoryDistribution: FormArray<
		FormGroup<{
			competenceCategoryId: FormControl<string>;
			percentage: FormControl<number>;
		}>
	>;
	isKnockoutQuestion: FormControl<boolean>;
	ratingItemIconSetId: FormControl<string>;
	procedure: FormControl<string>;
	hintsAndReasoning: FormControl<string>;
};

@UntilDestroy()
@Injectable()
export class AssessmentContextService extends AssessableContextService<AssessmentItem, AssessmentItemFormState> {
	// State
	private _isInitialized = false;
	private _assessment = new BehaviorSubject<Assessment>(null);
	private _assignedCompetenceCategoryIds: string[] = [];
	private _hasAnyImportedItems = signal(false);
	private _errorIds = signal<string[]>([]);
	private _isFormValid = signal(false);

	// injections
	private _fb = inject(FormBuilder);
	private _dialogService = inject(SimMaterialDialogService);
	private _simNotificationService = inject(NotificationService);
	private _translateService = inject(TranslateService);
	private _simAssessmentService = inject(SimAssessmentService);
	private _assessmentDataService = inject(AssessmentDataService);
	private _simRecordingsService = inject(SimRecordingsService);

	public $assessable: Observable<Assessment> = combineLatest([
		this._assessment.asObservable(),
		defer(() => this.form.valueChanges.pipe(startWith(null))),
	]).pipe(
		// do not use the form value from the combineLatest, as it might not be in sync with the form
		map(([assessment]) => this._getModelWithFormState(assessment, this.form.value)),
		tap((assessment) => this._checkForImportedItems(assessment)),
		tap(() => this._checkForErrors()),
	);
	public $savingPipeline = defer(() => this.form.valueChanges).pipe(
		debounceTime(500),
		filter(() => this.form.valid),
		map((value) => this._getModelWithFormState(this._assessment.getValue(), value)),
		tap((assessment) => this.saveAssessable(assessment)),
	);
	public form: FormGroup<AssessableFormState<AssessmentItemFormState>>;

	public hasAnyImportedItems = this._hasAnyImportedItems.asReadonly();
	public errorIds = this._errorIds.asReadonly();
	public isFormValid = this._isFormValid.asReadonly();

	/**
	 * Get an immutable copy of the current assessable, merged with the form state
	 */
	public getAssessable(): Assessment {
		const assessment = this._assessment.getValue();
		return this._getModelWithFormState(assessment, this.form.value);
	}

	public initialize(assessment: Assessment | UserRecordingAssessment, assignedCompetenceCategoryIds: string[]) {
		if (this._isInitialized) {
			return;
		}

		this._assessment.next(assessment);
		this._assignedCompetenceCategoryIds = assignedCompetenceCategoryIds;
		this.form = this._fb.group({
			assessableGroups: this._fb.array(assessment.assessableGroups.map((group) => this._createAssessmentGroupFormGroup(group))),
			ungroupedItems: this._fb.array(assessment.ungroupedItems.map((item) => this._createAssessmentItemFormGroup(item))),
		});

		this.form.statusChanges.pipe(untilDestroyed(this), startWith(this.form.status)).subscribe((status) => {
			this._isFormValid.set(status === "VALID");
		});

		if (TypePredicates.isUserRecordingAssessment(assessment)) {
			this._simRecordingsService.getRecordingById(assessment.scenarioRecordingId).then((recording) => {
				this._scenarioRecording = recording;
			});
		}

		this._isInitialized = true;
	}

	/**
	 * Update the form with the given assessment.
	 * @param assessment
	 */
	public setAssessment(assessment: Assessment) {
		this.resetChangedCompetenceCategoriesInForm(assessment.competenceCategoryIds, assessment);
		const noEmit = {emitEvent: false};

		for (const group of assessment.assessableGroups) {
			const groupControl = this.form.controls.assessableGroups.controls.find(
				(groupControl) => groupControl.controls.id.value === group.groupId,
			);
			if (!groupControl) {
				// we don't have the group nor its items in the form, so we add it
				this.form.controls.assessableGroups.push(this._createAssessmentGroupFormGroup(group), noEmit);
			} else {
				// we have the group in the form, so we update it
				const newGroup = this._createAssessmentGroupFormGroup(group).value;
				groupControl.controls.name.setValue(newGroup.name, noEmit);
				groupControl.controls.note.setValue(newGroup.note, noEmit);
				for (const item of group.items) {
					const itemControl = this.findItemControl(item.assessableItemId);
					if (!itemControl) {
						groupControl.controls.items.push(this._createAssessmentItemFormGroup(item), noEmit);
					} else {
						const newItem = this._createAssessmentItemFormGroup(item).value;
						itemControl.controls.title.setValue(newItem.title, noEmit);
						itemControl.controls.note.setValue(newItem.note, noEmit);
						itemControl.controls.freeTextMaxScore.setValue(newItem.freeTextMaxScore, noEmit);
						itemControl.controls.isKnockoutQuestion.setValue(newItem.isKnockoutQuestion, noEmit);

						itemControl.controls.yesNoScores.patchValue(newItem.yesNoScores, noEmit);
						itemControl.controls.starScores.patchValue(newItem.starScores, noEmit);
						itemControl.controls.selectableOptions.patchValue(newItem.selectableOptions, noEmit);
						itemControl.controls.competenceCategoryDistribution.patchValue(newItem.competenceCategoryDistribution, noEmit);
					}
				}
			}
		}

		this._assessment.next(assessment);
		this.saveAssessable(assessment).then();
	}

	public resetChangedCompetenceCategoriesInForm(newIds: string[], newAssessment: Assessment) {
		if (!this.form) {
			return;
		}
		// do nothing if the assigned competence categories haven't changed
		if (
			difference(newIds, this._assignedCompetenceCategoryIds).length === 0 &&
			difference(this._assignedCompetenceCategoryIds, newIds).length === 0
		) {
			return;
		}

		this._assignedCompetenceCategoryIds = newIds;

		const itemControls = this.form.controls.assessableGroups.controls
			.flatMap((group) => group.controls.items.controls)
			.concat(this.form.controls.ungroupedItems.controls);
		for (const itemControl of itemControls) {
			const item = this._findItemInModel(newAssessment, itemControl.controls.id.value);
			itemControl.controls.competenceCategoryDistribution.clear({emitEvent: false});
			const newDistribution = this._createCompetenceCategoryDistributionFormArray(item);
			for (const entry of newDistribution.controls) {
				itemControl.controls.competenceCategoryDistribution.push(entry);
			}
		}
	}

	public async addGroup() {
		const assessment = cloneDeep(this._assessment.getValue());

		let group = await this._simAssessmentService.addAssessableGroup({
			assessableId: assessment.id,
			name: "",
		});
		// 1. update the model
		assessment.assessableGroups.push(group);
		this._assessment.next(assessment);

		// 2. update the form
		this.form.controls.assessableGroups.push(this._createAssessmentGroupFormGroup(group));

		return group.groupId;
	}

	public removeGroup(index: number) {
		this.form.controls.assessableGroups.removeAt(index);
	}

	public async addItemToGroup(groupIndex: number) {
		const assessment = this.getAssessable();
		const group = assessment.assessableGroups[groupIndex];

		let serverResponseItem = await this._simAssessmentService.addAssessableItem({
			assessableId: assessment.id,
			title: "",
			type: ChecklistItemType.ThreeStarOption,
			groupId: group.groupId,
			note: null,
		});
		// 1. update the model
		const item = this._getItemWithChangedType(serverResponseItem, ChecklistItemType.ThreeStarOption);
		assessment.assessableGroups[groupIndex].items.push(item);
		this._assessment.next(assessment);

		// 2. update the form
		this.form.controls.assessableGroups.controls[groupIndex].controls.items.push(this._createAssessmentItemFormGroup(item));
		return {
			groupId: group.groupId,
			itemId: item.assessableItemId,
		};
	}

	public removeItem({itemIndex, groupIndex}: RemoveItemParams) {
		if (groupIndex === null) {
			this.form.controls.ungroupedItems.removeAt(itemIndex);
		} else {
			const groupControl = this.form.controls.assessableGroups.controls.at(groupIndex);
			groupControl.controls.items.removeAt(itemIndex);
		}
	}

	public addSelectableOption(itemControl: FormGroup<AssessmentItemFormState>, withMinValidator = true) {
		itemControl.controls.selectableOptions.push(this._createSelectableOptionFormGroup(withMinValidator));
	}

	public removeSelectableOption(itemControl: FormGroup<AssessmentItemFormState>, index: number) {
		itemControl.controls.selectableOptions.removeAt(index);
	}

	public addThreeStarOption(itemControl: FormGroup<AssessmentItemFormState>) {
		itemControl.controls.starScores.push(this._createPointInputControl(0, 0));
	}

	public removeThreeStarOption(itemControl: FormGroup<AssessmentItemFormState>) {
		itemControl.controls.starScores.removeAt(itemControl.controls.starScores.length - 1);
	}

	public dropGroupPanel(event: CdkDragDrop<FormArray>) {
		if (event.previousContainer === event.container) {
			FormArrayHelpers.moveItemInFormArray(event.container.data, event.previousIndex, event.currentIndex);
		}
	}

	public dropUngroupedItem(event: CdkDragDrop<FormArray>) {
		if (event.previousContainer === event.container) {
			FormArrayHelpers.moveItemInFormArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			FormArrayHelpers.transferFormArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex,
			);
		}
	}

	public dropItemInsideGroup(event: CdkDragDrop<FormArray>) {
		if (event.previousContainer === event.container) {
			FormArrayHelpers.moveItemInFormArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			FormArrayHelpers.transferFormArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex,
			);
		}
	}

	public shiftGroup(dir: "up" | "down", groupIndex: number) {
		FormArrayHelpers.moveItemInFormArray(this.form.controls.assessableGroups, groupIndex, groupIndex + this._getShiftDirection(dir));
	}

	public shiftGroupedItem(dir: "up" | "down", groupIndex: number, itemIndex: number) {
		const groupControl = this.form.controls.assessableGroups.controls.at(groupIndex);
		FormArrayHelpers.moveItemInFormArray(groupControl.controls.items, itemIndex, itemIndex + this._getShiftDirection(dir));
	}

	public shiftUngroupedItem(dir: "up" | "down", itemIndex: number) {
		FormArrayHelpers.moveItemInFormArray(this.form.controls.ungroupedItems, itemIndex, itemIndex + this._getShiftDirection(dir));
	}

	/**
	 * This method changes the type of item and updates the form and model accordingly
	 */
	public handleItemTypeChange(item: AssessmentItem, selectedChecklistItemType: ChecklistItemType) {
		// get updated item model
		const updatedItem = this._getItemWithChangedType(item, selectedChecklistItemType);

		// update model
		const assessment = this.getAssessable();
		const group = assessment.assessableGroups.find((g) => g.items.some((i) => i.assessableItemId === updatedItem.assessableItemId));
		if (group) {
			const itemIndex = group.items.findIndex((i) => i.assessableItemId === updatedItem.assessableItemId);
			group.items[itemIndex] = updatedItem;
		} else {
			const itemIndex = assessment.ungroupedItems.findIndex((i) => i.assessableItemId === updatedItem.assessableItemId);
			assessment.ungroupedItems[itemIndex] = updatedItem;
		}
		this._assessment.next(assessment);

		// handle form updates
		this._updateItemTypeInForm(updatedItem, selectedChecklistItemType);
	}

	/**
	 * Saves the assessable to the server.
	 */
	public async saveAssessable(assessment: Assessment) {
		await this._simAssessmentService.updateAssessment({
			assessmentId: assessment.id,
			assessableGroups: assessment.assessableGroups,
		});
	}

	public async moveItemToOtherGroup(itemIndex: number, groupIndex: number) {
		const assessment = this.getAssessable();
		const result = await this._dialogService.openMoveChecklistItemToGroup({
			checklistGroups: assessment.assessableGroups,
		});
		if (result.action !== "ok") {
			return;
		}
		const group = assessment.assessableGroups[groupIndex];
		const item = cloneDeep(group.items[itemIndex]);
		const {selectedChecklistGroupIndex} = result;
		if (group.groupId === result.selectedChecklistGroup.groupId) {
			return;
		}
		this.removeItem({itemIndex, groupIndex});

		this.form.controls.assessableGroups.at(selectedChecklistGroupIndex).controls.items.push(this._createAssessmentItemFormGroup(item));
	}

	public async changeActivationCondition(group: AssessableItemGroup<AssessmentItem>) {
		const assessment = this.getAssessable();
		let result = await this._dialogService.openChangeActivationConditionDialog({
			assessable: assessment,
			group: group,
		});

		if (result.action !== "ok") {
			return;
		}

		try {
			const updatedAssessment = await this._simAssessmentService.updateAssessmentGroupActivationCondition({
				assessmentId: assessment.id,
				groupId: group.groupId,
				activationCondition: result.activationCondition,
			});
			this.setAssessment(updatedAssessment);
			this._simNotificationService.notifySuccess(this._translateService.instant(Translations.SHARED_EXPANSION_SAVE_SUCCESS));
		} catch (e) {
			this._simNotificationService.notifyError(this._translateService.instant("Could not update activation condition"));
		}
	}

	public async changeRatingItemIcons(item: AssessmentItem) {
		const result = await this._dialogService.openGenericSelectionDialog({
			availableItems: this._assessmentDataService.getIconSets(),
			selectedItemId: item.ratingItemIconSetId,
			titleToken: Translations.ASSESSMENT_CHANGE_RATING_ITEM_ICONS_DIALOG_TITLE,
		});

		if (result.action !== "ok") {
			return;
		}

		const itemControl = this.findItemControl(item.assessableItemId);
		itemControl.controls.ratingItemIconSetId.setValue(result.selectedItemId);

		if (result.selectedItemId) {
			// default scores for 'smiley' icons
			// 'smiley' is are currently the only supported ratingItemIconSet
			// TODO: if the icon set will be changeable in the future, we also need to change this
			const scores = [0, 1, 2];
			itemControl.controls.starScores.clear();
			scores.map((score) => itemControl.controls.starScores.push(this._createPointInputControl(score, 0)));
		}
	}

	/// Form State Helpers
	/**
	 * Gets the model overwritten by the form state.
	 * Assumes that the every item in the form is also present in the model.
	 * Does not itself update the form state or the model.
	 */
	private _getModelWithFormState(assessment: Assessment, formValue: FormGroup<AssessableFormState<AssessmentItemFormState>>["value"]) {
		const mergedAssessment = merge({}, assessment, formValue);
		mergedAssessment.assessableGroups = formValue.assessableGroups.map(mapGroup);
		mergedAssessment.ungroupedItems = formValue.ungroupedItems.map(mapItem);
		return mergedAssessment;

		// local functions
		function mapItem(formItem: (typeof formValue.ungroupedItems)[0]) {
			const items = assessment.assessableGroups.flatMap((g) => g.items).concat(assessment.ungroupedItems);
			const modelItem = items.find((i) => i.assessableItemId === formItem.id);
			if (!modelItem) {
				throw new Error("Item not found in model");
			}

			const mergedItem = merge({}, modelItem, formItem);
			mergedItem.starScores = formItem.starScores;
			mergedItem.selectableOptions = formItem.selectableOptions as ValuedSelectableOption[];
			return mergedItem;
		}

		function mapGroup(formGroup: (typeof formValue.assessableGroups)[0]) {
			const modelGroup = assessment.assessableGroups.find((g) => g.groupId === formGroup.id);
			if (!modelGroup) {
				throw new Error("Group not found in model");
			}

			const mergedGroup = merge({}, modelGroup, formGroup);
			mergedGroup.items = formGroup.items.map(mapItem);
			return mergedGroup;
		}
	}

	private _createAssessmentGroupFormGroup(assessmentGroup: AssessableItemGroup<AssessmentItem>) {
		return this._fb.group({
			// Required for keeping track of the group
			id: assessmentGroup.groupId,
			name: [assessmentGroup.name ?? "", Validators.required],
			note: assessmentGroup.note ?? "",
			items: this._fb.array(assessmentGroup.items.map((item) => this._createAssessmentItemFormGroup(item))),
		});
	}

	private _createAssessmentItemFormGroup(assessmentItem: AssessmentItem): FormGroup<AssessmentItemFormState> {
		return this._fb.group({
			// Required for keeping track of the item
			id: assessmentItem.assessableItemId,
			title: [assessmentItem.title ?? "", Validators.required],
			note: assessmentItem.note ?? "",
			selectableOptions: this._fb.array(
				assessmentItem.selectableOptions.map((option) =>
					this._createSelectableOptionFormGroup(assessmentItem.type === ChecklistItemType.SingleChoiceOption, option),
				),
			),
			freeTextMaxScore: this._createPointInputControl(assessmentItem.freeTextMaxScore, 0),
			starScores: this._fb.array(assessmentItem.starScores.map((score) => this._createPointInputControl(score, 0))),
			yesNoScores: this._fb.array(assessmentItem.yesNoScores.map((score) => this._createPointInputControl(score, 0))),
			isKnockoutQuestion: assessmentItem.isKnockoutQuestion,
			competenceCategoryDistribution: this._createCompetenceCategoryDistributionFormArray(assessmentItem),
			ratingItemIconSetId: assessmentItem.ratingItemIconSetId,
			procedure: assessmentItem.procedure,
			hintsAndReasoning: assessmentItem.hintsAndReasoning,
		});
	}

	private _createCompetenceCategoryDistributionFormArray(assessmentItem: AssessmentItem) {
		const evenSplit = Math.floor(10_000 / this._assignedCompetenceCategoryIds.length);
		const remainder = 10_000 % this._assignedCompetenceCategoryIds.length;

		const formArray = this._fb.array(
			this._assignedCompetenceCategoryIds.map((id, index) => {
				const isLast = index === this._assignedCompetenceCategoryIds.length - 1;
				const category = assessmentItem.competenceCategoryDistribution.find((dist) => dist.competenceCategoryId === id);
				const percentage = category?.percentage ?? evenSplit + (isLast ? remainder : 0);
				return this._fb.group({
					competenceCategoryId: id,
					percentage: [percentage, [Validators.min(0), Validators.max(10_000)]],
				});
			}),
			[
				(control: AssessmentItemFormState["competenceCategoryDistribution"]) => {
					if (control.controls.length === 0) {
						return null;
					}

					const sum = control.controls.reduce((acc, formGroup) => acc + formGroup.controls.percentage.value, 0);
					const remaining = 10_000 - sum;
					return remaining >= 0 && remaining <= 1 ? null : {invalidDistribution: true};
				},
			],
		);

		return formArray;
	}

	private _createSelectableOptionFormGroup(
		withMinValidator = true,
		value: ValuedSelectableOption = {
			option: "",
			score: 0,
		},
	) {
		return this._fb.group({
			option: [value.option, Validators.required],
			score: this._createPointInputControl(value.score, withMinValidator ? 0 : undefined),
		});
	}

	private _createPointInputControl(value: number, min?: number) {
		return this._fb.control(value, [PointInputValidator, ...(min !== undefined ? [Validators.min(min)] : [])]);
	}

	/// Other helpers
	private _findItemInModel(assessment: Assessment, id: string): AssessmentItem {
		const items = assessment.assessableGroups.flatMap((group) => group.items).concat(assessment.ungroupedItems);
		return items.find((i) => i.assessableItemId === id);
	}

	private _getShiftDirection(dir: "up" | "down"): -1 | 1 {
		return dir === "up" ? -1 : 1;
	}

	/**
	 * Updates the form according to the selected checklist item type.
	 */
	private _updateItemTypeInForm(item: AssessmentItem, selectedChecklistItemType: ChecklistItemType) {
		const itemControl = this.findItemControl(item.assessableItemId);
		switch (selectedChecklistItemType) {
			case ChecklistItemType.SingleChoiceOption:
			case ChecklistItemType.MultipleChoiceOption:
				// re add the options
				itemControl.controls.selectableOptions.clear();
				item.selectableOptions.forEach((option) =>
					itemControl.controls.selectableOptions.push(
						this._createSelectableOptionFormGroup(selectedChecklistItemType === ChecklistItemType.SingleChoiceOption, option),
					),
				);
				itemControl.controls.starScores.clear();
				itemControl.controls.yesNoScores.clear();
				itemControl.controls.freeTextMaxScore.setValue(0);
				break;

			case ChecklistItemType.ThreeStarOption:
				itemControl.controls.starScores.clear();
				item.starScores.map((score) => itemControl.controls.starScores.push(this._createPointInputControl(score, 0)));
				itemControl.controls.yesNoScores.clear();
				itemControl.controls.freeTextMaxScore.setValue(0);
				itemControl.controls.selectableOptions.clear();
				break;

			case ChecklistItemType.YesNoOption:
				itemControl.controls.yesNoScores.clear();
				item.yesNoScores.map((score) => itemControl.controls.yesNoScores.push(this._createPointInputControl(score, 0)));
				itemControl.controls.freeTextMaxScore.setValue(0);
				itemControl.controls.starScores.clear();
				itemControl.controls.selectableOptions.clear();
				break;

			case ChecklistItemType.FreeAnswerOption:
				itemControl.controls.freeTextMaxScore.setValue(0);
				itemControl.controls.starScores.clear();
				itemControl.controls.yesNoScores.clear();
				itemControl.controls.selectableOptions.clear();
				break;
		}
	}

	/**
	 * Changes the type of the item and returns the updated item.
	 * This method does not update the form or model.
	 */
	private _getItemWithChangedType(item: AssessmentItem, itemType: ChecklistItemType): AssessmentItem {
		const mutableItem = cloneDeep(item);
		mutableItem.type = itemType;

		switch (mutableItem.type) {
			case ChecklistItemType.SingleChoiceOption:
			case ChecklistItemType.MultipleChoiceOption:
				mutableItem.starScores = [];
				mutableItem.yesNoScores = [];
				mutableItem.freeTextMaxScore = 0;

				// create 3 empty options if there are none
				if (mutableItem.selectableOptions.length === 0) {
					mutableItem.selectableOptions = [
						{option: "", score: 0},
						{option: "", score: 0},
						{option: "", score: 0},
					];
				}
				// keep existing selectableOptions
				break;

			case ChecklistItemType.ThreeStarOption:
				mutableItem.starScores = [0, 1, 2];
				mutableItem.yesNoScores = [];
				mutableItem.freeTextMaxScore = 0;
				mutableItem.selectableOptions = [];
				break;

			case ChecklistItemType.YesNoOption:
				mutableItem.starScores = [];
				mutableItem.yesNoScores = [0, 0];
				mutableItem.freeTextMaxScore = 0;
				mutableItem.selectableOptions = [];
				break;

			case ChecklistItemType.FreeAnswerOption:
			default:
				mutableItem.starScores = [];
				mutableItem.yesNoScores = [];
				mutableItem.freeTextMaxScore = 0;
				mutableItem.selectableOptions = [];
				break;
		}

		return mutableItem;
	}

	private _checkForImportedItems(assessment: Assessment) {
		const items = assessment.assessableGroups.flatMap((group) => group.items).concat(assessment.ungroupedItems);
		const hasAnyImportedItems = items.some((item) => item.fromCatalogueId !== null);
		this._hasAnyImportedItems.set(hasAnyImportedItems);
	}

	private _checkForErrors() {
		if (this.form.valid) {
			this._errorIds.set([]);
			return;
		}
		const errorIds: string[] = [];
		searchErrorId(this.form, errorIds);
		this._errorIds.set(errorIds);
	}
}

function searchErrorId(control: AbstractControl, errorIds: string[]): void {
	if (control.get("id") && !control.valid) {
		errorIds.push(control.get("id").value);
	}
	if (control instanceof FormArray) {
		control.controls.forEach((c) => searchErrorId(c, errorIds));
	}
	if (control instanceof FormGroup) {
		Object.values(control.controls).forEach((c) => searchErrorId(c, errorIds));
	}
}
